import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import { searchNews, searchWorks } from "@/utils/anime";
import { usePageMeta } from "@/hooks/usePageMeta";

const NEWS_PAGE = 6;
const WORK_PAGE = 8;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";

  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<"news" | "works">("news");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(keyword);
    setPage(1);
  }, [keyword]);

  usePageMeta(
    keyword ? `搜索“${keyword}” - 78动漫` : "搜索 - 78动漫",
    keyword
      ? `78动漫搜索“${keyword}”的结果，包含相关动漫资讯与动画作品。`
      : "在78动漫搜索动漫资讯与动画作品，快速找到你感兴趣的内容。",
    "动漫搜索,资讯搜索,作品搜索,78动漫",
  );

  const newsResults = useMemo(() => searchNews(keyword), [keyword]);
  const workResults = useMemo(() => searchWorks(keyword), [keyword]);

  const activeResults = tab === "news" ? newsResults : workResults;
  const pageSize = tab === "news" ? NEWS_PAGE : WORK_PAGE;
  const totalPages = Math.max(1, Math.ceil(activeResults.length / pageSize));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = input.trim();
    if (!next) return;
    setSearchParams({ q: next });
    setPage(1);
  };

  const changeTab = (next: "news" | "works") => {
    setTab(next);
    setPage(1);
  };

  const changePage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pagedNews = newsResults.slice((page - 1) * NEWS_PAGE, page * NEWS_PAGE);
  const pagedWorks = workResults.slice((page - 1) * WORK_PAGE, page * WORK_PAGE);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-10">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">全站搜索</h1>
            <p className="mt-2 text-sm text-foreground-500">支持同时搜索动漫资讯与动画作品</p>
            <form onSubmit={handleSubmit} className="mt-5 w-full max-w-2xl">
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 focus-within:border-primary-400">
                  <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                    <i className="ri-search-line text-lg" />
                  </span>
                  <input
                    type="text"
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    placeholder="输入关键词，如：剧场版、奇幻、芙莉莲"
                    className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                  />
                </div>
                <button
                  type="submit"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-search-line" />
                  搜索
                </button>
              </div>
            </form>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-12">
          {keyword && (
            <p className="text-sm text-foreground-600">
              关键词 <strong className="font-semibold text-primary-600">“{keyword}”</strong> 的搜索结果
            </p>
          )}

          <div className="mt-5 flex items-center gap-2 rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => changeTab("news")}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                tab === "news" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              <i className="ri-newspaper-line" />
              动漫资讯
              <span
                className={`rounded-full px-1.5 text-xs ${
                  tab === "news" ? "bg-background-50/25" : "bg-background-200 text-foreground-600"
                }`}
              >
                {newsResults.length}
              </span>
            </button>
            <button
              type="button"
              onClick={() => changeTab("works")}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                tab === "works" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              <i className="ri-movie-line" />
              动漫作品
              <span
                className={`rounded-full px-1.5 text-xs ${
                  tab === "works" ? "bg-background-50/25" : "bg-background-200 text-foreground-600"
                }`}
              >
                {workResults.length}
              </span>
            </button>
          </div>

          {!keyword ? (
            <EmptyState
              icon="ri-search-line"
              title="输入关键词开始搜索"
              description="试试搜索「剧场版」「奇幻」「芙莉莲」等关键词，发现相关资讯与作品。"
            />
          ) : activeResults.length === 0 ? (
            <EmptyState
              icon="ri-emotion-sad-line"
              title={`没有找到与“${keyword}”相关的内容`}
              description="换个关键词试试，或返回首页浏览最新资讯与热门作品。"
            />
          ) : tab === "news" ? (
            <>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {pagedNews.map((news) => (
                  <NewsCard key={news.id} news={news} />
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={changePage} />
            </>
          ) : (
            <>
              <div className="mt-6 flex flex-col gap-4">
                {pagedWorks.map((work) => (
                  <Link
                    key={work.id}
                    to={`/recommendations/detail/${work.id}`}
                    className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-all hover:-translate-y-0.5 hover:border-primary-300 sm:flex-row cursor-pointer"
                  >
                    <span className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-100 sm:h-36 sm:w-28">
                      <img
                        src={work.cover}
                        alt={`${work.title} 封面`}
                        title={`${work.title} - 78动漫作品推荐`}
                        className="h-full w-full object-cover object-top"
                      />
                    </span>
                    <span className="flex flex-1 flex-col">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="rounded-md bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                          {work.type}
                        </span>
                        <span className="flex items-center gap-1 rounded-md bg-accent-100 px-2 py-0.5 text-xs font-bold text-accent-800">
                          <i className="ri-star-fill" />
                          {work.score.toFixed(1)}
                        </span>
                        <span className="text-xs text-foreground-400">
                          {work.year} · {work.region}
                        </span>
                      </span>
                      <span className="mt-2 font-heading text-lg font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                        {work.title}
                      </span>
                      <span className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-500">
                        {work.synopsis}
                      </span>
                      <span className="mt-3 flex flex-wrap gap-1.5">
                        {work.genres.map((genre) => (
                          <span
                            key={genre}
                            className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800"
                          >
                            {genre}
                          </span>
                        ))}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={changePage} />
            </>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function EmptyState({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-16 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
        <i className={`${icon} text-3xl`} />
      </span>
      <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">{title}</h2>
      <p className="mt-2 max-w-md text-sm text-foreground-500">{description}</p>
      <Link
        to="/"
        className="mt-6 flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
      >
        <i className="ri-home-4-line" />
        返回首页
      </Link>
    </div>
  );
}